import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { HighchartsChartModule } from 'highcharts-angular';
import Highcharts from 'highcharts';
import { MetricsCardComponent } from '../dashboard/metrics-card/metrics-card.component';
import { FabricDataService } from '../fabric-data.service';
import { UtilService } from '../services/util.service';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { FabricApiResponse, Metric } from '../models/api.models';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ReactiveFormsModule, FormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { TrendDirection } from '../models/types';
import { catchError, of, forkJoin, Observable } from 'rxjs';
import { PeriodRangePickerComponent, PeriodRangeValue } from '../shared/components/period-range-picker/period-range-picker.component';

// Define static date range boundaries for the picker
const MIN_DATE = new Date('2023-12-06');
const MAX_DATE = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 1); // Set to yesterday
  return d;
})();

interface MetricWithTrend {
  label: string;
  type: string;
  value: number | undefined;
  trend: TrendDirection;
  description?: string;
}

@Component({
  selector: 'app-energy-usage-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    HighchartsChartModule,
    MetricsCardComponent,
    MatFormField,
    MatLabel,
    DatePipe,
    MatDatepickerModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatError,
    PeriodRangePickerComponent
  ],
  templateUrl: './energy-usage-dashboard.component.html',
  styleUrls: ['./energy-usage-dashboard.component.scss']
})
export class EnergyUsageDashboardComponent implements OnInit {
  // --- Annual metrics filter state ---
  annualEditView = false;
  annualFromDate = '';
  annualToDate = '';
  annualMinDate: Date = MIN_DATE;
  annualMaxDate: Date = MAX_DATE;
  periodRange: PeriodRangeValue | null = null;
  previousPeriodRange: PeriodRangeValue | null = null;
  
  isLoading = true;
  isChartsLoading = true;
  editView = false; // Add editView flag to match dashboard pattern
  
  // Highcharts configuration
  Highcharts: typeof Highcharts = Highcharts;
  chartConstructor = 'chart';
  updateFlag = false;
  
  // Chart configurations
  profileClassChart: Highcharts.Options = {};
  dualFuelUsageChart: Highcharts.Options = {};
  electricityUsageChart: Highcharts.Options = {};
  
  // Main metrics (matching dashboard structure)
  metrics = {
    totalSwitches: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    totalMPANs: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    totalMPRNs: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    smartMeterMPANs: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    smartMeterMPRNs: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    retentionSwitches: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    acquisitionSwitches: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    dualFuelSwitches: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    elecOnlySwitches: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    // Average consumption metrics
    avgElectricityUsage: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    avgGasUsage: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    totalCombinedUsage: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
  };
  
  // Grouped metrics for the energy usage dashboard
  metricGroups: {
    savings: Array<MetricWithTrend>
    usage: Array<MetricWithTrend>
    carbonFootprint: Array<MetricWithTrend>
  } = {
    savings: [],
    usage: [],
    carbonFootprint: []
  };

  // Date range for API calls
  fromDate = '';
  toDate = '';
  
  // Date range picker properties
  minDate: Date = MIN_DATE;
  maxDate: Date = MAX_DATE;
  range = new FormGroup({
    start: new FormControl<Date | null>(null, Validators.required),
    end: new FormControl<Date | null>(null, Validators.required)
  });

  constructor(
    private fabricDataService: FabricDataService, 
    private util: UtilService
  ) {}

  ngOnInit(): void {
    this.prepareDateRange();
    this.prepareAnnualRange();
    // Set default options for all charts
    Highcharts.setOptions({
      credits: { enabled: false }
    });
    this.loadMetricsData();
    this.loadAverageConsumptionData();
    this.loadChartData(); // Now using proper endpoints
    this.loadEnergyUsageData();
  }

  prepareDateRange(): void {
    // Set default date range (last week)
    const endDate = new Date();
    endDate.setDate(endDate.getDate() - 1); // Yesterday
    
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 7); // 7 days ago
    
    // Ensure we don't go before the minimum date
    if (startDate < MIN_DATE) {
      startDate.setTime(MIN_DATE.getTime());
    }
    
    this.fromDate = this.util.formatDate(startDate);
    this.toDate = this.util.formatDate(endDate);
    
    // Set initial values for the date range picker form
    this.range.setValue({
      start: startDate,
      end: endDate
    });
  }

  /**
   * Prepare the default annual range (YTD: Last year today till yesterday)
   */
  prepareAnnualRange(): void {
    // Initialize with default YTD range
    const lastYear = new Date().getFullYear() - 1;
    const today = new Date();
    const startDate = new Date(lastYear, today.getMonth(), today.getDate());
    const endDate = new Date();
    endDate.setDate(endDate.getDate() - 1);
    
    // Clamp to min/max
    const clampedStart = startDate < MIN_DATE ? new Date(MIN_DATE) : startDate;
    const clampedEnd = endDate > MAX_DATE ? new Date(MAX_DATE) : endDate;
    
    this.annualFromDate = this.util.formatDate(clampedStart);
    this.annualToDate = this.util.formatDate(clampedEnd);
    
    // Set initial period range
    this.periodRange = {
      periodType: 'ytd',
      rangeStart: null,
      rangeEnd: null,
      dateRange: {
        startDate: clampedStart,
        endDate: clampedEnd
      }
    };
  }

  /**
   * Handle period range change from the custom component
   */
  onPeriodRangeChange(range: PeriodRangeValue | null): void {
    this.periodRange = range;
  }

  /**
   * Enter annual edit mode and backup current value
   */
  enterAnnualEditMode(): void {
    // Deep clone the current value to preserve it
    this.previousPeriodRange = this.periodRange ? {
      periodType: this.periodRange.periodType,
      rangeStart: this.periodRange.rangeStart ? { ...this.periodRange.rangeStart } : null,
      rangeEnd: this.periodRange.rangeEnd ? { ...this.periodRange.rangeEnd } : null,
      dateRange: this.periodRange.dateRange ? {
        startDate: new Date(this.periodRange.dateRange.startDate),
        endDate: new Date(this.periodRange.dateRange.endDate)
      } : null
    } : null;
    this.annualEditView = true;
  }

  /**
   * Cancel annual edit and reset to previous state
   */
  cancelAnnualEdit(): void {
    // Restore the previous value
    this.periodRange = this.previousPeriodRange;
    this.annualEditView = false;
  }

  /**
   * Apply the selected annual date range and reload annual metrics.
   */
  applyAnnualDateChange(): void {
    if (!this.periodRange?.dateRange) {
      return;
    }

    this.annualFromDate = this.util.formatDate(this.periodRange.dateRange.startDate);
    this.annualToDate = this.util.formatDate(this.periodRange.dateRange.endDate);
    this.annualEditView = false;
    
    // Reload annual metrics with the selected range
    this.loadEnergyUsageData();
  }

  /**
   * Apply the selected date range and reload data.
   */
  applyDateChange(): void {
    if (this.range.valid && this.range.value.start && this.range.value.end) {
      this.fromDate = this.util.formatDate(this.range.value.start);
      this.toDate = this.util.formatDate(this.range.value.end);

      // Close the edit view
      this.editView = false;

      // Reload daywise data based on the new date range
      this.loadMetricsData();
      this.loadAverageConsumptionData();
      this.loadChartData();
    }
  }

  /**
   * Load metrics data (switches, MPANs, MPRNs) using 'all' as supplier
   */
  loadMetricsData(): void {
    this.resetMainMetrics();
    // Define metric endpoints to fetch
    const metricEndpoints = {
      mpans: 'elec_meter_types_switch_count',
      mprns: 'gas_meter_types_switch_count',
      total: 'total_switches'
    };
    
    // Create API request object for forkJoin with error handling
    const apiRequests: Record<string, Observable<FabricApiResponse<Metric[] | any>>> = {};
    
    // Build API requests with error handling, using 'all' as supplier parameter
    Object.entries(metricEndpoints).forEach(([key, endpoint]) => {
      apiRequests[key] = this.fabricDataService.fetchFabricDataWithDate(endpoint, this.fromDate, this.toDate, 'all')
        .pipe(
          catchError(error => {
            console.error(`Error fetching ${endpoint} data:`, error);
            this.util.openSnackBar(`Error fetching ${endpoint.replace('_', ' ')} metrics`, 'Close', 'error');
            return of({ result: { data: [] } });
          })
        ) as Observable<FabricApiResponse<Metric[]>>;
    });
    
    // Fetch data from all APIs in parallel
    forkJoin(apiRequests).subscribe({
      next: (results) => {
        // Process MPAN data
        if (results?.['mpans']?.result?.data) {
          const mpanData = results['mpans'].result.data as Metric[];
          // Set total MPANs (from TotalCount type)
          const totalMpan = mpanData.find(item => item.type === 'TotalCount');
          if (totalMpan) {
            this.metrics.totalMPANs.value = totalMpan.count;
          }
          
          // Set Smart Meter MPANs
          const smartMpan = mpanData.find(item => item.type === 'SmartMeter');
          if (smartMpan) {
            this.metrics.smartMeterMPANs.value = smartMpan.count;
          }
        }
        
        // Process MPRN data
        if (results?.['mprns']?.result?.data) {
          const mprnData = results['mprns'].result.data as Metric[];
          // Set total MPRNs (from TotalCount type)
          const totalMprn = mprnData.find(item => item.type === 'TotalCount');
          if (totalMprn) {
            this.metrics.totalMPRNs.value = totalMprn.count;
          }
          
          // Set Smart Meter MPRNs
          const smartMprn = mprnData.find(item => item.type === 'SmartMeter');
          if (smartMprn) {
            this.metrics.smartMeterMPRNs.value = smartMprn.count;
          }
        }

        // Process total switches data
        if (results?.['total'].result?.data) {
          const totalSwitchData = results['total'].result.data as any;
          const totalSwitches = totalSwitchData['totalSwitchIdCounts'] || 0;
          const retentionSwitches = totalSwitchData['retentionSwitchIdCounts'] || 0;
          const acquisitionSwitches = totalSwitchData['acquisitionSwitchIdCounts'] || 0;
          const dualFuelSwitches = totalSwitchData['duelFuelSwitchIdCounts'] || 0;
          const elecOnlySwitches = totalSwitchData['elecSwitchIdCounts'] || 0;

          this.metrics.totalSwitches.value = totalSwitches;
          this.metrics.retentionSwitches.value = retentionSwitches;
          this.metrics.acquisitionSwitches.value = acquisitionSwitches;
          this.metrics.dualFuelSwitches.value = dualFuelSwitches;
          this.metrics.elecOnlySwitches.value = elecOnlySwitches;
        }
      },
      error: (error) => {
        console.error('Unexpected error in loadMetricsData:', error);
      }
    });
  }

  /**
   * Load average consumption data using 'all' as supplier
   */
  loadAverageConsumptionData(): void {
    this.fabricDataService.fetchFabricDataWithDate('energy_usage_dashboard_meters_infos', this.fromDate, this.toDate, 'all')
      .pipe(
        catchError(error => {
          console.error('Error fetching average consumption data:', error);
          this.util.openSnackBar('Error fetching average consumption data', 'Close', 'error');
          return of({ result: { data: [] } });
        })
      )
      .subscribe({
        next: (response) => {
          if (response.result?.data) {
            const data = response.result.data as Metric[];
            
            // Process each metric type
            data.forEach(metric => {
              switch (metric.type) {
                case 'avgUsageElec':
                  this.metrics.avgElectricityUsage.value = metric.count ? Math.round(metric.count * 100) / 100 : 0;
                  break;
                case 'avgUsageGas':
                  this.metrics.avgGasUsage.value = metric.count ? Math.round(metric.count * 100) / 100 : 0;
                  break;
                case 'totalCombined':
                  this.metrics.totalCombinedUsage.value = metric.count ? Math.round(metric.count * 100) / 100 : 0;
                  break;
              }
            });
          }
        },
        error: (error) => {
          console.error('Unexpected error in loadAverageConsumptionData:', error);
        }
      });
  }

  /**
   * Load chart data for the three usage charts using proper endpoints
   */
  loadChartData(): void {
    this.isChartsLoading = true;
    this.resetCharts();
    
    const chartRequests: Record<string, Observable<FabricApiResponse<Highcharts.Options>>> = {};
    
    // Profile class bar chart
    chartRequests['profileClass'] = this.fabricDataService.fetchFabricChartWithDate(
      'energy_usage_by_profile_class',
      this.fromDate,
      this.toDate,
      'all'
    ).pipe(
      catchError(error => {
        console.error('Error fetching profile class chart:', error);
        this.util.openSnackBar('Error fetching profile class chart', 'Close', 'error');
        return of({ result: { data: null } } as any);
      })
    );
    
    // Dual fuel usage line chart
    chartRequests['dualFuel'] = this.fabricDataService.fetchFabricChartWithDate(
      'energy_usage_dual_fuel',
      this.fromDate,
      this.toDate,
      'all'
    ).pipe(
      catchError(error => {
        console.error('Error fetching dual fuel chart:', error);
        this.util.openSnackBar('Error fetching dual fuel chart', 'Close', 'error');
        return of({ result: { data: null } } as any);
      })
    );
    
    // Electricity usage line chart
    chartRequests['electricity'] = this.fabricDataService.fetchFabricChartWithDate(
      'energy_usage_electricity_only',
      this.fromDate,
      this.toDate,
      'all'
    ).pipe(
      catchError(error => {
        console.error('Error fetching electricity chart:', error);
        this.util.openSnackBar('Error fetching electricity chart', 'Close', 'error');
        return of({ result: { data: null } } as any);
      })
    );
    
    forkJoin(chartRequests).subscribe({
      next: (results) => {
        // Update profile class chart
        if (results['profileClass']?.result?.data) {
          this.updateChartOptions(this.profileClassChart, results['profileClass']);
        }
        
        // Update dual fuel chart
        if (results['dualFuel']?.result?.data) {
          this.updateChartOptions(this.dualFuelUsageChart, results['dualFuel']);
        }
        
        // Update electricity chart
        if (results['electricity']?.result?.data) {
          this.updateChartOptions(this.electricityUsageChart, results['electricity']);
        }
        
        this.updateFlag = true;
        this.isChartsLoading = false;
      },
      error: (error) => {
        console.error('Unexpected error in loadChartData:', error);
        this.isChartsLoading = false;
      }
    });
  }
  
  /**
   * Helper method to update chart options with data and common settings
   */
  private updateChartOptions(chartOptions: Highcharts.Options, data: any): boolean {
    if (!data?.result?.data) {
      chartOptions.series = [];
      return false;
    }
    
    // Update chart options with data
    Object.assign(chartOptions, data.result.data);
    
    // Convert any string formatters to functions (reused from dashboard)
    this.convertStringFormatters(chartOptions);
    
    // Apply common chart settings
    chartOptions.chart = {
      ...chartOptions.chart,
      backgroundColor: '#f7fafa'
    };
    
    return true;
  }
  
  /**
   * Convert string formatters to functions (reused from dashboard)
   */
  private convertStringFormatters(obj: any): void {
    if (!obj) return;

    const convertFormatter = (formatterStr: string): Function => {
      const functionBody = formatterStr
        .replace(/^function\s*\(\s*\)\s*\{/, '')
        .replace(/\}$/, '')
        .trim();
      return new Function(functionBody);
    };

    if (obj.formatter && typeof obj.formatter === 'string') {
      obj.formatter = convertFormatter(obj.formatter);
    }

    if (Array.isArray(obj)) {
      obj.forEach(item => this.convertStringFormatters(item));
      return;
    }
    
    if (typeof obj === 'object') {
      Object.entries(obj).forEach(([key, value]) => {
        if (value && typeof value === 'object') {
          this.convertStringFormatters(value);
        }
      });
    }
  }
  
  /**
   * Reset chart configurations
   */
  private resetCharts(): void {
    this.profileClassChart = {};
    this.dualFuelUsageChart = {};
    this.electricityUsageChart = {};
    this.updateFlag = false;
  }

  /**
   * Load energy usage data from the API
   */
  loadEnergyUsageData(): void {
    this.isLoading = true;
    this.resetMetrics();

    const from = this.annualFromDate || this.fromDate;
    const to = this.annualToDate || this.toDate;

    this.fabricDataService.fetchFabricDataWithDate('energy_usage_dashboard', from, to)
      .pipe(
        catchError(error => {
          console.error('Error fetching energy usage data:', error);
          this.util.openSnackBar('Error fetching energy usage data', 'Close', 'error');
          return of({ result: { data: [] } });
        })
      )
      .subscribe({
        next: (response) => {
          if (response.result?.data) {
            this.processEnergyUsageData(response.result.data);
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Unexpected error in loadEnergyUsageData:', error);
          this.isLoading = false;
        }
      });
  }

  /**
   * Process the energy usage data and map to metrics dynamically
   */
  private processEnergyUsageData(data: Metric[]): void {
    // Reset all groups
    this.resetMetrics();
    
    data.forEach(metric => {
      const processedMetric = {
        label: metric.label,
        type: metric.type,
        value: metric.count ? Math.round(metric.count * 100) / 100 : 0,
        trend: 'neutral' as TrendDirection,
        description: metric.description
      };

      // Group metrics based on their type or label
      if (this.isSavingMetric(metric.type)) {
        this.metricGroups.savings.push(processedMetric);
      } else if (this.isUsageMetric(metric.type)) {
        this.metricGroups.usage.push(processedMetric);
      } else if (this.isCarbonFootprintMetric(metric.type)) {
        this.metricGroups.carbonFootprint.push(processedMetric);
      }
    });
  }

  /**
   * Determine if a metric is a saving metric
   */
  private isSavingMetric(type: string): boolean {
    const savingsTypes = [
      'TotalAnnualElecSaving',
      'TotalAnnualGasSaving',
      'TotalAnnualEnergySaving'
    ];
    return savingsTypes.includes(type);
  }

  /**
   * Determine if a metric is a usage metric
   */
  private isUsageMetric(type: string): boolean {
    const usageTypes = [
      'Total_Elec_Usage_GW',
      'TotalGasUsage_GW',
      'TotalEnergyUsage_GW'
    ];
    return usageTypes.includes(type);
  }

  /**
   * Determine if a metric is a carbon footprint metric
   */
  private isCarbonFootprintMetric(type: string): boolean {
    const carbonFootprintTypes = [
      'SupplierWiseElecCFP',
      'CFP_Elec_Difference',
      'TotalElecCarbonFootprint',
      'TotalGasCarbonFootprint'
    ];
    return carbonFootprintTypes.includes(type);
  }

  /**
   * Reset main metrics values to undefined
   */
  private resetMainMetrics(): void {
    this.metrics.totalSwitches.value = undefined;
    this.metrics.totalMPANs.value = undefined;
    this.metrics.totalMPRNs.value = undefined;
    this.metrics.smartMeterMPANs.value = undefined;
    this.metrics.smartMeterMPRNs.value = undefined;
    this.metrics.retentionSwitches.value = undefined;
    this.metrics.acquisitionSwitches.value = undefined;
    this.metrics.dualFuelSwitches.value = undefined;
    this.metrics.elecOnlySwitches.value = undefined;
    // Reset average consumption metrics
    this.metrics.avgElectricityUsage.value = undefined;
    this.metrics.avgGasUsage.value = undefined;
    this.metrics.totalCombinedUsage.value = undefined;
  }

  /**
   * Reset all metric values to undefined
   */
  private resetMetrics(): void {
    this.metricGroups = {
      savings: [],
      usage: [],
      carbonFootprint: []
    };
  }
}
